<template>
  <Teleport to="body">
    <div class="element-inspector-container">
      <!-- 🔍 钢笔调试工具（可拖拽） -->
      <div 
        v-if="isEnabled"
        class="debug-pen-tool"
        :style="{ top: penPosition.y + 'px', left: penPosition.x + 'px' }"
        @mousedown="startDragPen"
        @touchstart.prevent="startDragPen"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.37 3.29L20.71 5.63C21.1 6.02 21.1 6.65 20.71 7.04L7.04 20.71C6.65 21.1 6.02 21.1 5.63 20.71L3.29 18.37C2.9 18 2.9 17.37 3.29 16.98L16.98 3.29C17.37 2.9 18 2.9 18.37 3.29Z" stroke="#4a9eff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 6L18 16" stroke="#4a9eff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3 21L8 16" stroke="#4a9eff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <!-- 关闭按钮 -->
        <button 
          class="debug-pen-close-btn"
          @click.stop="handleClose"
          @touchstart.stop="handleClose"
          title="关闭调试模式"
        >
          ×
        </button>
      </div>

      <!-- 🔍 元素信息面板 -->
      <div 
        v-if="isEnabled && debugElementInfo.tagName"
        class="debug-info-panel"
        :style="{ top: debugInfoPosition.y + 'px', left: debugInfoPosition.x + 'px' }"
      >
        <div class="debug-info-header">
          <span>✏️ {{ debugElementInfo.tagName }}</span>
          <button @click="closeDebugInfo" class="debug-close-btn">×</button>
        </div>
        <div class="debug-info-content">
          <div class="debug-info-item" v-if="debugElementInfo.className">
            <strong>类名:</strong> {{ debugElementInfo.className }}
          </div>
          <div class="debug-info-item" v-if="debugElementInfo.id">
            <strong>ID:</strong> {{ debugElementInfo.id }}
          </div>
          <div class="debug-info-item">
            <strong>位置:</strong> top: {{ debugElementInfo.top }}px, left: {{ debugElementInfo.left }}px
          </div>
          <div class="debug-info-item">
            <strong>尺寸:</strong> {{ debugElementInfo.width }}px × {{ debugElementInfo.height }}px
          </div>
          <div class="debug-info-item">
            <strong>Z-index:</strong> {{ debugElementInfo.zIndex }}
          </div>
          <div class="debug-info-item" v-if="debugElementInfo.backgroundColor && debugElementInfo.backgroundColor !== 'transparent' && debugElementInfo.backgroundColor !== 'rgba(0, 0, 0, 0)'">
            <strong>背景色:</strong> {{ debugElementInfo.backgroundColor }}
          </div>
          <div class="debug-info-item" v-if="debugElementInfo.opacity && debugElementInfo.opacity !== '1'">
            <strong>透明度:</strong> {{ debugElementInfo.opacity }}
          </div>
        </div>
      </div>

      <!-- 🔍 调试模式提示 -->
      <div 
        v-if="isEnabled"
        class="debug-mode-indicator"
      >
        ✏️ 调试模式已开启 - 拖拽钢笔图标到任意元素查看信息（点击右上角 × 关闭）
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

// Props
const props = defineProps({
  enabled: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['close']);

// 内部状态
const isEnabled = ref(props.enabled);
const debugElementInfo = ref({
  tagName: '',
  className: '',
  id: '',
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  zIndex: 'auto',
  backgroundColor: '',
  opacity: ''
});
const debugInfoPosition = ref({ x: 0, y: 0 });

// 钢笔图标拖拽状态
const penPosition = ref({ x: 50, y: 50 }); // 钢笔图标初始位置
const isDraggingPen = ref(false); // 是否正在拖拽钢笔
const highlightedElement = ref(null); // 当前高亮的元素
const highlightStyle = ref(null); // 保存原始样式，用于恢复

// 监听 enabled prop 变化
watch(() => props.enabled, (newVal) => {
  isEnabled.value = newVal;
  if (!newVal) {
    // 退出调试模式时清除高亮和信息
    clearHighlight();
    closeDebugInfo();
    isDraggingPen.value = false;
  }
});

// 🔍 获取元素信息
const getElementInfo = (element) => {
  if (!element) return null;
  
  const styles = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  
  return {
    tagName: element.tagName.toLowerCase(),
    className: element.className || '',
    id: element.id || '',
    top: Math.round(rect.top),
    left: Math.round(rect.left),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    zIndex: styles.zIndex || 'auto',
    backgroundColor: styles.backgroundColor || 'transparent',
    opacity: styles.opacity || '1'
  };
};

// 🔍 清除元素高亮
const clearHighlight = () => {
  if (highlightedElement.value && highlightStyle.value) {
    // 恢复原始样式
    highlightedElement.value.style.outline = highlightStyle.value.outline;
    highlightedElement.value.style.outlineOffset = highlightStyle.value.outlineOffset;
    highlightedElement.value.style.boxShadow = highlightStyle.value.boxShadow;
    highlightedElement.value = null;
    highlightStyle.value = null;
  }
};

// 🔍 更新信息面板位置，确保在可视区域内且距离钢笔图标至少20px
const updateInfoPanelPosition = () => {
  if (!debugElementInfo.value.tagName) return;
  
  // 等待 DOM 更新后计算面板实际尺寸
  setTimeout(() => {
    const panel = document.querySelector('.debug-info-panel');
    if (panel) {
      const panelRect = panel.getBoundingClientRect();
      const panelWidth = panelRect.width;
      const panelHeight = panelRect.height;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const penSize = 50;
      const minDistance = 20; // 最小距离20px
      
      // 钢笔图标的边界
      const penLeft = penPosition.value.x;
      const penRight = penPosition.value.x + penSize;
      const penTop = penPosition.value.y;
      const penBottom = penPosition.value.y + penSize;
      
      // 计算初始位置（优先放在钢笔图标右侧）
      let x = penRight + minDistance;
      let y = penTop;
      
      // 如果右侧空间不足，尝试放在左侧
      if (x + panelWidth > viewportWidth) {
        x = penLeft - panelWidth - minDistance;
        // 如果左侧也不够，放在屏幕右侧边缘
        if (x < 0) {
          x = Math.max(10, viewportWidth - panelWidth - 10);
        }
      }
      
      // 确保不超出左边界
      if (x < 0) {
        x = 10;
      }
      
      // 检查Y轴方向是否与钢笔图标重叠，确保至少20px距离
      const panelTop = y;
      const panelBottom = y + panelHeight;
      
      // 如果面板与钢笔图标在Y轴方向重叠，调整位置
      if (!(panelBottom < penTop - minDistance || panelTop > penBottom + minDistance)) {
        // 重叠了，尝试放在下方
        if (penBottom + minDistance + panelHeight <= viewportHeight) {
          y = penBottom + minDistance;
        } else {
          // 下方空间不够，放在上方
          y = Math.max(10, penTop - panelHeight - minDistance);
        }
      }
      
      // 确保不超出下边界
      if (y + panelHeight > viewportHeight) {
        y = Math.max(10, viewportHeight - panelHeight - 10);
      }
      
      // 确保不超出上边界
      if (y < 0) {
        y = 10;
      }
      
      debugInfoPosition.value = { x, y };
    }
  }, 0);
};

// 🔍 高亮元素
const highlightElement = (element) => {
  // 清除之前的高亮
  clearHighlight();
  
  if (!element || element === document.body || element === document.documentElement) {
    return;
  }
  
  // 排除调试相关的元素
  if (element.closest('.debug-pen-tool') || 
      element.closest('.debug-info-panel') || 
      element.closest('.debug-mode-indicator')) {
    return;
  }
  
  highlightedElement.value = element;
  
  // 保存原始样式
  const computedStyle = window.getComputedStyle(element);
  highlightStyle.value = {
    outline: element.style.outline || '',
    outlineOffset: element.style.outlineOffset || '',
    boxShadow: element.style.boxShadow || ''
  };
  
  // 添加高亮边框
  element.style.outline = '3px solid #4a9eff';
  element.style.outlineOffset = '2px';
  element.style.boxShadow = '0 0 0 2px rgba(74, 158, 255, 0.3)';
  
    // 获取元素信息
    const info = getElementInfo(element);
    if (info) {
      debugElementInfo.value = info;
      // 更新面板位置
      updateInfoPanelPosition();
    }
};

// 🔍 检测鼠标下的元素
const detectElementAtPosition = (x, y) => {
  // 临时隐藏钢笔图标，避免检测到自己
  const penTool = document.querySelector('.debug-pen-tool');
  const penToolDisplay = penTool ? penTool.style.display : null;
  if (penTool) {
    penTool.style.display = 'none';
  }
  
  try {
    // 获取鼠标位置下的元素
    const element = document.elementFromPoint(x, y);
    
    // 恢复钢笔图标显示
    if (penTool && penToolDisplay !== null) {
      penTool.style.display = penToolDisplay;
    }
    
    if (element) {
      highlightElement(element);
    }
  } catch (error) {
    // 如果出错，恢复钢笔图标显示
    if (penTool && penToolDisplay !== null) {
      penTool.style.display = penToolDisplay;
    }
  }
};

// 🔍 钢笔图标拖拽开始
const startDragPen = (event) => {
  if (!isEnabled.value) return;
  
  // 如果点击的是关闭按钮，不触发拖拽
  if (event.target.closest('.debug-pen-close-btn')) {
    return;
  }
  
  event.preventDefault();
  event.stopPropagation();
  
  isDraggingPen.value = true;
  
  // 获取触摸或鼠标位置
  const clientX = event.touches ? event.touches[0].clientX : event.clientX;
  const clientY = event.touches ? event.touches[0].clientY : event.clientY;
  
  // 更新钢笔位置（现在图标是50px，所以要减去25px）
  penPosition.value = {
    x: clientX - 25, // 减去图标宽度的一半
    y: clientY - 25  // 减去图标高度的一半
  };
  
  // 检测当前位置的元素
  detectElementAtPosition(clientX, clientY);
};

// 🔍 钢笔图标拖拽中
const dragPen = (event) => {
  if (!isDraggingPen.value || !isEnabled.value) return;
  
  event.preventDefault();
  
  // 获取触摸或鼠标位置
  const clientX = event.touches ? event.touches[0].clientX : event.clientX;
  const clientY = event.touches ? event.touches[0].clientY : event.clientY;
  
  // 计算钢笔位置（现在图标是50px，所以要减去25px）
  let penX = clientX - 25;
  let penY = clientY - 25;
  
  // 确保钢笔图标不超出屏幕边界
  const penSize = 50;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  penX = Math.max(0, Math.min(penX, viewportWidth - penSize));
  penY = Math.max(0, Math.min(penY, viewportHeight - penSize));
  
  // 更新钢笔位置
  penPosition.value = {
    x: penX,
    y: penY
  };
  
  // 检测当前位置的元素
  detectElementAtPosition(clientX, clientY);
  
  // 如果信息面板已显示，更新其位置
  if (debugElementInfo.value.tagName) {
    updateInfoPanelPosition();
  }
};

// 🔍 钢笔图标拖拽结束
const stopDragPen = (event) => {
  if (!isDraggingPen.value) return;
  
  event.preventDefault();
  event.stopPropagation();
  isDraggingPen.value = false;
};

// 🔍 处理触摸移动（移动端）
const handleTouchMove = (event) => {
  if (isDraggingPen.value && isEnabled.value) {
    dragPen(event);
  }
};

// 🔍 处理触摸结束（移动端）
const handleTouchEnd = (event) => {
  if (isDraggingPen.value) {
    stopDragPen(event);
  }
};

// 🔍 关闭调试信息
const closeDebugInfo = () => {
  debugElementInfo.value = {
    tagName: '',
    className: '',
    id: '',
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    zIndex: 'auto',
    backgroundColor: '',
    opacity: ''
  };
};

// 🔍 关闭调试模式
const handleClose = () => {
  isEnabled.value = false;
  clearHighlight();
  closeDebugInfo();
  isDraggingPen.value = false;
  emit('close');
};

// 🔍 监听全局鼠标移动（用于拖拽钢笔）
const handleGlobalMouseMove = (event) => {
  if (isDraggingPen.value && isEnabled.value) {
    dragPen(event);
  }
};

// 🔍 监听全局鼠标抬起（用于结束拖拽）
const handleGlobalMouseUp = (event) => {
  if (isDraggingPen.value) {
    stopDragPen(event);
  }
};

// 监听键盘事件（按 D 键切换调试模式）
const handleKeydown = (event) => {
  if ((event.key === 'd' || event.key === 'D') && event.ctrlKey) {
    event.preventDefault();
    isEnabled.value = !isEnabled.value;
    if (!isEnabled.value) {
      handleClose();
    }
  }
};

// 监听全局事件
onMounted(() => {
  document.addEventListener('mousemove', handleGlobalMouseMove);
  document.addEventListener('mouseup', handleGlobalMouseUp);
  document.addEventListener('touchmove', handleTouchMove, { passive: false });
  document.addEventListener('touchend', handleTouchEnd);
  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleGlobalMouseMove);
  document.removeEventListener('mouseup', handleGlobalMouseUp);
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('touchend', handleTouchEnd);
  document.removeEventListener('keydown', handleKeydown);
  // 清除元素高亮
  clearHighlight();
});
</script>

<style>
/* 容器样式 - 确保不干扰页面布局 */
.element-inspector-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 999999;
}
/* 🔍 钢笔调试工具样式 */
.debug-pen-tool {
  position: fixed;
  width: 50px;
  height: 50px;
  cursor: grab;
  z-index: 999999 !important; /* 设置极高的层级，确保在最上层 */
  pointer-events: auto;
  user-select: none;
  transition: transform 0.1s ease;
  filter: drop-shadow(0 2px 8px rgba(74, 158, 255, 0.5));
  /* 添加背景色，确保可见性 */
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 5px;
  box-shadow: 0 4px 20px rgba(74, 158, 255, 0.6), 0 0 20px rgba(74, 158, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: penFloat 0.3s ease, penPulse 2s ease-in-out infinite;
}

.debug-pen-tool:active {
  cursor: grabbing;
  transform: scale(1.15);
  animation: none;
}

.debug-pen-tool:hover {
  transform: scale(1.1);
  filter: drop-shadow(0 4px 12px rgba(74, 158, 255, 0.7));
  animation: none;
}

.debug-pen-tool svg {
  width: 30px;
  height: 30px;
  display: block;
  /* 确保 SVG 在白色背景上清晰可见 */
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

/* 🔍 关闭按钮样式 */
.debug-pen-close-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 59, 48, 0.9);
  color: white;
  border: 2px solid white;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 1000000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  pointer-events: auto;
}

.debug-pen-close-btn:hover {
  background: rgba(255, 59, 48, 1);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(255, 59, 48, 0.5);
}

.debug-pen-close-btn:active {
  transform: scale(0.95);
}

@keyframes penFloat {
  from {
    opacity: 0;
    transform: scale(0.5) rotate(-10deg);
  }
  to {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

/* 🔍 钢笔图标脉冲动画 - 让用户更容易发现 */
@keyframes penPulse {
  0%, 100% {
    box-shadow: 0 4px 20px rgba(74, 158, 255, 0.6), 0 0 20px rgba(74, 158, 255, 0.4);
  }
  50% {
    box-shadow: 0 4px 30px rgba(74, 158, 255, 0.9), 0 0 40px rgba(74, 158, 255, 0.7);
  }
}

/* 🔍 调试模式指示器 */
.debug-mode-indicator {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 999997 !important; /* 确保在很高层级 */
  pointer-events: none;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* 🔍 调试信息面板 */
.debug-info-panel {
  position: fixed;
  background: rgba(0, 0, 0, 0.95);
  color: #fff;
  border-radius: 12px;
  padding: 0;
  z-index: 999998 !important; /* 仅次于钢笔图标，确保在最上层 */
  min-width: 300px;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideIn 0.3s ease;
  pointer-events: auto;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.debug-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
  font-size: 14px;
}

.debug-close-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.debug-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.debug-info-content {
  padding: 10px 14px;
  font-size: 12px;
  line-height: 1.5;
  max-height: 400px;
  overflow-y: auto;
}

.debug-info-item {
  margin-bottom: 8px;
  word-break: break-word;
}

.debug-info-item:last-child {
  margin-bottom: 0;
}

.debug-info-item strong {
  color: #4a9eff;
  margin-right: 6px;
  display: inline-block;
  min-width: 60px;
  font-weight: 600;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .debug-info-panel {
    min-width: 280px;
    max-width: calc(100vw - 20px);
  }
  
  .debug-info-header {
    padding: 8px 12px;
    font-size: 13px;
  }
  
  .debug-info-content {
    padding: 8px 12px;
    font-size: 11px;
    line-height: 1.4;
  }
  
  .debug-info-item {
    margin-bottom: 6px;
  }
  
  .debug-info-item strong {
    min-width: 50px;
    font-size: 11px;
  }
}
</style>

